import {
  createTicketApi,
  fetchTicketsApi,
  updateTicketAssigneeApi,
  updateTicketStatusApi
} from 'configs/axios/api_helper'

const TICKETS_UPDATED_EVENT = 'anomalyTicketsUpdated'

const toApiError = (error, fallbackMessage) => {
  if (!error) return new Error(fallbackMessage)
  const responseErrors =
    error?.response?.data?.errors ||
    error?.data?.errors ||
    (Array.isArray(error?.errors) ? error.errors : null)
  const responseMessage = error?.response?.data?.message || error?.data?.message
  const message =
    (Array.isArray(responseErrors) && responseErrors.length ? responseErrors[0] : null) ||
    responseMessage ||
    error?.message ||
    fallbackMessage
  return new Error(message)
}

const extractResponseData = (response, fallbackMessage) => {
  if (!response || response.isAxiosError || response instanceof Error) {
    throw toApiError(response, fallbackMessage)
  }
  return response.data
}

const unwrapTicketPayload = (payload, fallbackMessage) => {
  const data = payload?.data
  if (!data) {
    throw new Error(fallbackMessage)
  }
  return data
}

const normalizeTicket = (ticket = {}) => {
  const description = ticket.description || ''
  const resolutionComment = ticket.resolutionComment ?? ticket.resolution_comment ?? null

  return {
    ...ticket,
    description,
    summary: ticket.summary || description,
    resolutionComment
  }
}

export const fetchAnomalyTickets = async (filters = {}) => {
  const response = await fetchTicketsApi(filters)
  const payload = extractResponseData(response, 'Failed to load tickets')
  const rows = Array.isArray(payload?.data) ? payload.data : []
  return rows.map(normalizeTicket)
}

export const createAnomalyTicket = async (ticketPayload = {}) => {
  try {
    const response = await createTicketApi(ticketPayload)
    const data = unwrapTicketPayload(response, 'Failed to create ticket')
    return normalizeTicket(data)
  } catch (error) {
    throw toApiError(error, 'Failed to create ticket')
  }
}

const updateTicketStatus = async (ticketId, { status, resolutionComment }) => {
  if (status === undefined) return null
  try {
    const response = await updateTicketStatusApi(ticketId, {
      status,
      resolutionComment
    })
    const data = unwrapTicketPayload(response, 'Failed to update ticket status')
    return normalizeTicket(data)
  } catch (error) {
    throw toApiError(error, 'Failed to update ticket status')
  }
}

const updateTicketAssignee = async (ticketId, assignee) => {
  if (assignee === undefined) return null
  try {
    const response = await updateTicketAssigneeApi(ticketId, { assignee })
    const data = unwrapTicketPayload(response, 'Failed to update ticket assignee')
    return normalizeTicket(data)
  } catch (error) {
    throw toApiError(error, 'Failed to update ticket assignee')
  }
}

export const updateAnomalyTicket = async (ticketId, updates = {}) => {
  const { status, resolutionComment, assignee } = updates || {}
  const operations = []

  if (status !== undefined) {
    operations.push(() => updateTicketStatus(ticketId, { status, resolutionComment }))
  }

  if (assignee !== undefined) {
    operations.push(() => updateTicketAssignee(ticketId, assignee))
  }

  if (!operations.length) {
    throw new Error('No valid ticket fields supplied for update')
  }

  let latest = null
  for (const op of operations) {
    // Execute sequentially to preserve backend state order.
    const result = await op()
    if (result) {
      latest = result
    }
  }

  return latest
}

export const buildTicketFromNotification = (notification, status, options = {}) => {
  const timestamp = Date.now().toString(36)
  const humanTitle = notification?.humanMessage || notification?.message || `Anomaly ticket ${timestamp}`

  const summary =
    notification?.metadata?.details ||
    notification?.description ||
    notification?.message ||
    'No additional details provided.'

  const resolutionReason =
    status === 'resolved'
      ? options?.resolutionReason || 'Resolved directly from notification center.'
      : null

  return {
    title: humanTitle,
    description: summary,
    status,
    assignee: options?.assignee || null,
    notification_id: notification?.id || null,
    resolutionComment: resolutionReason
  }
}

export const createTicketFromNotification = async (notification, status, options = {}) => {
  const payload = buildTicketFromNotification(notification, status, options)
  return createAnomalyTicket(payload)
}

export const ticketRefreshEventName = TICKETS_UPDATED_EVENT

export const notifyTicketsChanged = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(TICKETS_UPDATED_EVENT))
}
